import { useEffect, useState, useCallback, useRef } from "react";
import { Button, Card, Spinner, Container, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { clearCart } from "../../../store/cartSlice";
import { useSelector, useDispatch } from "react-redux";
import { REACT_APP_RAZORPAY_KEY_ID } from "../../../rzp/Razorpay";
import {
  createRazorpayOrderApi,
  updateRazorpayPaymentApi,
} from "../../../rzp/CustomerRzp";

const RAZORPAY_METHOD_MAP = {
  upi: 2,
  card: 3,
  netbanking: 4,
  wallet: 5,
  emi: 6,
  paylater: 7,
};

const PaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const reduxCartItems = useSelector((state) => state.cart.items);

  const [order, setOrder] = useState(null);
  const [memberDts, setMemberDts] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const paymentInProgress = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load data
  useEffect(() => {
    const init = async () => {
      try {
        const storedOrder = localStorage.getItem("pendingOrder");
        const storedCustomer = localStorage.getItem("customer");

        if (!storedOrder) {
          toast.error("No pending order found");
          navigate("/checkout", { replace: true });
          return;
        }

        const parsedOrder = JSON.parse(storedOrder);
        if (parsedOrder.orderID !== Number(id)) {
          toast.error("Order mismatch. Please try again.");
          navigate("/checkout", { replace: true });
          return;
        }

        setOrder(parsedOrder);

        if (storedCustomer) {
          setMemberDts(JSON.parse(storedCustomer));
        }

        if (reduxCartItems && reduxCartItems.length > 0) {
          setCartItems(reduxCartItems);
        } else {
          const storedCart = localStorage.getItem("cart");
          if (storedCart) {
            const parsed = JSON.parse(storedCart);
            setCartItems(parsed.items || []);
          }
        }
      } catch (err) {
        console.error("Load error:", err);
        toast.error("Failed to load order details");
        navigate("/checkout", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [id, navigate, reduxCartItems]);

  // Razorpay helpers
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const getDeviceID = useCallback(() => {
    let deviceId = localStorage.getItem("deviceID");
    if (!deviceId) {
      deviceId =
        crypto.randomUUID?.() ||
        `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("deviceID", deviceId);
    }
    return deviceId;
  }, []);

  const getValidMobile = useCallback(() => {
    let mobile = memberDts?.mobileNumber || "";
    mobile = String(mobile).replace(/\D/g, "");
    if (mobile.startsWith("91") && mobile.length === 12) {
      mobile = mobile.substring(2);
    }
    return mobile.length === 10 ? mobile : "";
  }, [memberDts]);

  // Payment Flow
  const handlePayment = async () => {
    if (!order || paying || paymentInProgress.current) return;

    paymentInProgress.current = true;
    setPaying(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !isMounted.current) {
        throw new Error("Razorpay SDK not available");
      }

      const customerID = memberDts?.ramNamBookMemberID || 0;

      const createRes = await createRazorpayOrderApi(
        customerID,
        order.orderID,
        order.finalTotal,
        getDeviceID(),
      );

      const orderData = createRes?.data;

      // ✅ FIX: Log full response to verify exact property names from API
      // console.log("createRazorpayOrderApi full response:", orderData);

      if (!orderData || !(orderData.IsSuccess || orderData.isSuccess)) {
        throw new Error(
          orderData?.Message ||
            orderData?.message ||
            "Failed to create payment order",
        );
      }

      // ✅ FIX: Support both PascalCase and camelCase from API response
      const razorOrderNo =
        orderData.RazorOrderNo ?? orderData.razorOrderNo ?? "";
      const receiptNo = orderData.ReceiptNo ?? orderData.receiptNo ?? "";
      let llcID = orderData.llcid ?? orderData.llcID ?? orderData.LlcID ?? orderData.LLCID ?? "";

      // console.log(
      //   "Extracted values → razorOrderNo:",
      //   razorOrderNo,
      //   "| receiptNo:",
      //   receiptNo,
      //   "| llcID:",
      //   llcID,
      // );

      if (!razorOrderNo) {
        throw new Error("Invalid Razorpay Order ID received from server");
      }

      const options = {
        key: REACT_APP_RAZORPAY_KEY_ID,
        amount: Math.round(order.finalTotal * 100),
        currency: "INR",
        name: "શ્રી રામવાળી મેલડી માઁ [બારેજા ધામ]",
        description: `Payment for Order #${order.orderNo}`,
        image: `${process.env.REACT_APP_WEB_URL}/assets/logo/barejadham1.png`,
        order_id: razorOrderNo,
        prefill: {
          name: memberDts?.memberName || "Customer",
          email: memberDts?.email || "",
          contact: getValidMobile(),
        },
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: false,
          emi: false,
          paylater: false,
        },
        theme: { color: "#FF4F00" },
        handler: async (response) => {
          if (!isMounted.current) return;
          // ✅ FIX: Pass the extracted variables (not from orderData again)
          await handlePaymentSuccess(
            response,
            customerID,
            razorOrderNo,
            receiptNo,
            llcID,
          );
        },
        modal: {
          ondismiss: () => {
            if (!isMounted.current) return;
            toast.info("Payment cancelled");
            setPaying(false);
            paymentInProgress.current = false;
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (err) => {
        if (!isMounted.current) return;
        toast.error(err?.error?.description || "Payment failed");
        setPaying(false);
        paymentInProgress.current = false;
      });

      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error(err.message || "Payment initiation failed");
      setPaying(false);
      paymentInProgress.current = false;

      if (
        err.message?.includes("create") ||
        err.message?.includes("Razorpay")
      ) {
        setTimeout(() => navigate("/checkout", { replace: true }), 2000);
      }
    }
  };

  const handlePaymentSuccess = async (
    response,
    customerID,
    razorOrderNo,
    receiptNo,
    llcID,
  ) => {
    try {
      // ✅ FIX: Log all values before sending to verify nothing is undefined
      // console.log("handlePaymentSuccess called with:", {
      //   razorpay_payment_id: response.razorpay_payment_id,
      //   customerID,
      //   orderID: order.orderID,
      //   razorOrderNo,
      //   receiptNo,
      //   llcID,
      // });

      let paymentMethod =
        response.method || response.razorpay_payment_method || "";
      const paymentModeID = RAZORPAY_METHOD_MAP[paymentMethod] || 0;

      const verifyRes = await updateRazorpayPaymentApi({
        customerID,
        orderID: order.orderID,
        razorTransactionID: response.razorpay_payment_id,
        razorOrderNo,
        receiptNo,
        llcID: String(llcID),
        paymentModeID,
      });

      // console.log("updateRazorpayPaymentApi response:", verifyRes?.data);

      const data = verifyRes?.data;
      if (!data || !(data.IsSuccess || data.isSuccess)) {
        throw new Error(
          data?.Message || data?.message || "Verification failed",
        );
      }

      // Success
      toast.success("Payment successful! Thank you.");
      localStorage.removeItem("pendingOrder");
      dispatch(clearCart());
      navigate("/member/dashboard", { replace: true });
    } catch (err) {
      console.error("Verification failed:", err);
      toast.error(
        err.message ||
          "Payment received but verification failed. Contact support.",
      );
      setPaying(false);
      paymentInProgress.current = false;
    }
  };

  const handleBack = () => {
    navigate("/checkout");
  };

  if (loading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center bg-light">
        <Spinner animation="border" variant="primary" size="lg" />
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Container fluid className="flex-grow-1 py-4 px-3 px-md-4">
        <div className="mx-auto" style={{ maxWidth: "1200px" }}>
          <div className="text-center mb-3">
            <h6 className="fw-bold text-success mb-1">Proceed to Payment</h6>
          </div>

          <Row className="g-4">
            {/* Left: Billing Details */}
            <Col xs={12} lg={4}>
              {memberDts && (
                <Card className="shadow border-0 rounded-3 h-100 overflow-hidden">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold text-dark mb-0">Billing Details</h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <div className="row g-3">
                      <div className="col-12">
                        <small className="text-muted d-block mb-1">Name</small>
                        <div className="fw-semibold">
                          {memberDts.memberName || "—"}
                        </div>
                      </div>
                      <div className="col-12">
                        <small className="text-muted d-block mb-1">
                          Mobile Number
                        </small>
                        <div className="fw-semibold">
                          {memberDts.mobileNumber || "—"}
                        </div>
                      </div>
                      {memberDts.villageCityName && (
                        <div className="col-12">
                          <small className="text-muted d-block mb-1">
                            Address
                          </small>
                          <div className="fw-semibold">
                            {memberDts.villageCityName},{" "}
                            {memberDts.addressAndText}, {memberDts.countryName}{" "}
                            - {memberDts.pincode}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* Right: Order Summary */}
            <Col xs={12} lg={8}>
              <Card className="shadow border-0 rounded-3 h-100">
                <Card.Header className="bg-white border-bottom py-3">
                  <h6 className="fw-bold text-dark mb-0">Order Summary</h6>
                </Card.Header>

                <Card.Body className="p-3 p-md-4 d-flex flex-column">
                  {cartItems.length > 0 ? (
                    <>
                      <div
                        className="flex-grow-1 mb-3"
                        style={{ maxHeight: "500px" }}
                      >
                        {cartItems.map((item, i) => (
                          <div key={item.id || i} className="py-3">
                            <div className="row align-items-center g-3">
                              <div className="col-4 col-md-2">
                                <div
                                  className="rounded-3 overflow-hidden bg-light border"
                                  style={{ aspectRatio: "1/1" }}
                                >
                                  {item.image ? (
                                    <img
                                      src={`${process.env.REACT_APP_API_URL || ""}${item.image}`}
                                      alt={item.name}
                                      className="w-100 h-100 object-fit-cover"
                                      onError={(e) => {
                                        e.target.src =
                                          "/images/placeholder-product.jpg";
                                      }}
                                    />
                                  ) : (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                                      No image
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="col-8 col-md-7">
                                <div className="fw-semibold">
                                  {item.name || "Item"}
                                </div>
                                {item.categoryName && (
                                  <div className="text-muted small">
                                    {item.categoryName}
                                  </div>
                                )}
                                <div className="text-muted small mt-1">
                                  {item.isFixedQty
                                    ? `Qty: ${Number(item.qty || 0)}`
                                    : `₹${Number(item.price || 0)} × ${item.qty || 1}`}
                                </div>
                              </div>

                              <div className="col-12 col-md-3 text-md-end fw-bold">
                                ₹
                                {item.isFixedQty
                                  ? (item.finalAmount || 0).toFixed(0)
                                  : (
                                      (item.price || 0) * (item.qty || 1)
                                    ).toFixed(0)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <hr className="my-3" />

                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fs-6 fs-md-5 fw-bold">
                          Total Amount
                        </span>
                        <span className="fs-5 fw-bold text-danger">
                          ₹
                          {Number(order.finalTotal || 0).toLocaleString(
                            "en-IN",
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No items in your order
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Sticky Footer */}
      <footer className="bg-white border-top shadow-lg py-3 px-3 px-md-4 sticky-bottom">
        <div
          className="row g-3 justify-content-center mx-auto"
          style={{ maxWidth: "1200px" }}
        >
          <div className="col-12 col-md-4">
            <Button
              variant="outline-secondary"
              size="lg"
              className="w-100 fw-bold shadow-lg"
              onClick={handleBack}
              disabled={paying}
              style={{ borderRadius: "10px" }}
            >
              ← Back
            </Button>
          </div>

          <div className="col-12 col-md-8">
            <Button
              size="lg"
              className="w-100 fw-bold shadow-lg"
              style={{
                background: "#FF4F00",
                border: "none",
                borderRadius: "10px",
                boxShadow: "0 6px 16px rgba(255,79,0,0.35)",
              }}
              onClick={handlePayment}
              disabled={paying}
            >
              {paying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                `Pay Now ₹${Number(order.finalTotal || 0).toLocaleString("en-IN")}`
              )}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentPage;
